import { Fragment, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PortalIdParser from 'PortalIdParser';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Loadable from 'UIComponents/decorators/Loadable';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import H2 from 'UIComponents/elements/headings/H2';
import { useAppContext } from '../../../context/AppContext';
import { useAppInstallability } from 'marketplace-ui-apps-core/installability/hooks/useAppInstallability';
import { UninstallableMessage } from 'marketplace-ui-apps-core/installability/components/UninstallableMessage';
import * as AppUninstallableReasons from 'ecosystem-marketplace-core/constants/installability/AppUninstallableReasons';
import PurchaseMotionButton from './PurchaseMotionButton';
// @ts-expect-error untyped import
import { handleConnectError } from '../../../actions/ConnectActions';
// @ts-expect-error untyped import
import { getEventSelectorId } from '../../../utils/analytics';
// @ts-expect-error untyped import
import useShortlinkParams from '../../../utils/hooks/useShortlinkParams';
import { getIsStandardConnection, getIsHomemadeExternalConnection, getIsHomemadeModalConnection
// @ts-expect-error untyped import
} from '../../../utils/offerings';
import { getFormattedUrl, getConnectedAppsUrl, getAppShortlink } from '../../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AsyncRequestPermissionsModal = Loadable({
  loader: () => import(
  // @ts-expect-error untyped import
  /* webpackChunkName: 'request-permissions-modal' */
  './RequestPermissionsModal')
});
const AsyncIntegrationsConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'integrations-connect-button' */'ui-addon-integrations-directory-panel/UIIntegrationsConnectButton')
});
const AsyncIntegrationsConnectModal = Loadable({
  loader: () => import( /* webpackChunkName: 'integrations-connect-modal' */'ui-addon-integrations-directory-panel/UIIntegrationsConnectModal')
});
const AsyncDataSyncConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'data-sync-connect-button' */'ui-universal-auth/DataSyncAuthButton')
});
const isEligibleForPurchaseMotion = uninstallableReason => uninstallableReason.label === AppUninstallableReasons.MISSING_HUB_SCOPES && uninstallableReason.missingScopes.includes('sales-navigator-install');
export function ConnectButton({
  appId,
  appName,
  appUrl,
  connectionType,
  isCustomer,
  isDataSyncApp,
  listingId,
  offeringId,
  onConnectButtonClick,
  onTrackEvent,
  onTrackInteraction,
  onTrackPageview,
  sticky
}) {
  const dispatch = useDispatch();
  const appContext = useAppContext();
  const {
    pathname
  } = useLocation();
  const shortlinkParams = useShortlinkParams();
  const [openIframeModal, setOpenIframeModal] = useState(false);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
  const {
    installabilityLoading,
    uninstallableReason
  } = useAppInstallability({
    appContext,
    offeringId: offeringId !== null && offeringId !== void 0 ? offeringId : 0
  });
  const standardConnection = getIsStandardConnection(connectionType);
  const homemadeExternalConnection = getIsHomemadeExternalConnection(connectionType);
  const homemadeModalConnection = getIsHomemadeModalConnection(connectionType);
  const handleTrackInteraction = useCallback(action => {
    onTrackInteraction(action, {
      section: 'header',
      value: appName
    });
  }, [appName, onTrackInteraction]);
  const onConnectFailed = useCallback(errorType => {
    dispatch(handleConnectError(errorType, appName));
  }, [appName, dispatch]);

  // Public connect button that redirects to portal picker
  if (!isCustomer) {
    return /*#__PURE__*/_jsx(UIButton, {
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      size: sticky ? 'small' : 'default',
      use: "primary",
      href: getAppShortlink(pathname, shortlinkParams),
      onClick: onConnectButtonClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.signIn"
      })
    });
  }

  // Render a loading button if we have not gotten a response from the installablity endpoint yet
  // This prevents vulnerabilities that can be caused by assuming that a user can install an app
  if (installabilityLoading) {
    return /*#__PURE__*/_jsx(UILoadingButton, {
      size: sticky ? 'small' : 'medium',
      use: "primary",
      disabled: true,
      loading: true,
      role: "button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.install"
      })
    });
  }
  if (uninstallableReason && uninstallableReason.label === AppUninstallableReasons.MISSING_USER_PERMISSIONS) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        size: sticky ? 'small' : 'default',
        use: "primary",
        onClick: () => {
          setOpenPermissionsModal(true);
          handleTrackInteraction('click request install app permission button');
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installButton.request"
        })
      }), openPermissionsModal && /*#__PURE__*/_jsx(AsyncRequestPermissionsModal, {
        listingId: listingId,
        missingHubUserRoles: uninstallableReason.missingHubUserRoles,
        onClose: () => setOpenPermissionsModal(false),
        onTrackEvent: onTrackEvent,
        onTrackPageview: onTrackPageview
      })]
    });
  }
  const handleConnectionComplete = payload => {
    const redirectUrl = payload && payload.meta && payload.meta.redirectUrl;
    window.location.href = redirectUrl || getConnectedAppsUrl(true);
  };

  // Simple button and disabled button for integrations
  if (uninstallableReason || standardConnection || homemadeExternalConnection) {
    const toolTip = /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(UninstallableMessage, {
        uninstallableReason: uninstallableReason
      }),
      disabled: !uninstallableReason,
      children: /*#__PURE__*/_jsx(UIButton, {
        "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
        size: sticky ? 'small' : 'default',
        use: "primary",
        disabled: !!uninstallableReason,
        external: standardConnection && !uninstallableReason,
        href: !uninstallableReason ? getFormattedUrl(appUrl) : null,
        onClick: onConnectButtonClick,
        role: "button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installButton.install"
        })
      })
    });
    if (uninstallableReason && isEligibleForPurchaseMotion(uninstallableReason)) {
      const purchaseMotionButton = /*#__PURE__*/_jsx(PurchaseMotionButton, {
        fallback: toolTip
      });
      return purchaseMotionButton;
    }
    return toolTip;
  }
  if (isDataSyncApp) {
    return /*#__PURE__*/_jsx(AsyncDataSyncConnectButton, {
      portalId: PortalIdParser.get(),
      appId: appId,
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      size: sticky ? 'small' : 'default',
      use: "primary",
      onClick: onConnectButtonClick,
      trackingSource: "marketplace",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.install"
      })
    });
  }

  // Iframe modal button
  if (homemadeModalConnection) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
        size: sticky ? 'small' : 'default',
        use: "primary",
        onClick: () => {
          setOpenIframeModal(true);
          onConnectButtonClick();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installButton.install"
        })
      }), openIframeModal && /*#__PURE__*/_jsxs(UIModal, {
        width: 600,
        use: "success",
        children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
          children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
            onClick: () => setOpenIframeModal(false)
          }), /*#__PURE__*/_jsx(H2, {
            "aria-level": 2,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-apps-core.installButton.modalTitle"
            })
          })]
        }), /*#__PURE__*/_jsx(UIDialogBody, {
          className: "p-bottom-4",
          children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectModal, {
            portalId: PortalIdParser.get(),
            appId: appId,
            sourceAppId: "app-marketplace",
            text: I18n.text('marketplace-ui-apps-core.installButton.install'),
            onConnectionComplete: handleConnectionComplete,
            onIntegrationNotFound: () => {
              console.log('not found');
            }
          })
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsx("div", {
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      style: {
        display: 'inline-block',
        verticalAlign: 'middle'
      },
      className: "m-left-3",
      children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectButton, {
        portalId: PortalIdParser.get(),
        appId: appId,
        sourceAppId: "app-marketplace",
        text: I18n.text('marketplace-ui-apps-core.installButton.install'),
        onClick: onConnectButtonClick,
        onConnectionComplete: handleConnectionComplete,
        onConnectionFailed: err => onConnectFailed(err.meta.type)
      })
    })
  });
}